import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Download, FileSignature } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { getErrorMessage } from '../lib/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_LABEL = {
  supplier_nca: 'Supplier Non-Circumvention Agreement',
  agent_nca: 'Agent Non-Circumvention Agreement',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function SignAgreement() {
  const { token } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [signatureDate, setSignatureDate] = useState(today());
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/agreements/${token}`)
      .then((res) => setAgreement(res.data))
      .catch(() => setError('This agreement link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSign = async (e) => {
    e.preventDefault();
    if (!signatureName || !signatureDate) {
      toast.error('Please type your full name and confirm the date');
      return;
    }
    if (!agreed) {
      toast.error('Please confirm you have read and agree to the terms above');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/agreements/${token}/sign`, { signature_name: signatureName, signature_date: signatureDate });
      setAgreement(res.data);
      toast.success('Agreement signed successfully!');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg text-brand-text flex items-center justify-center">
        <p className="font-mono text-sm text-brand-muted uppercase tracking-wider">Loading agreement…</p>
      </div>
    );
  }

  if (error || !agreement) {
    return (
      <div className="min-h-screen bg-brand-bg text-brand-text flex items-center justify-center px-5">
        <p className="text-brand-muted text-center" data-testid="sign-agreement-error">{error}</p>
      </div>
    );
  }

  const isSigned = agreement.status === 'signed';

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text font-body py-16 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        <div className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-2">
            {DOCUMENT_LABEL[agreement.document_type]}
          </p>
          <h1 className="font-display font-extrabold uppercase text-2xl text-brand-text">
            {isSigned ? 'Agreement Signed' : `Hi ${agreement.recipient_name}, please review & sign`}
          </h1>

          {!isSigned && (
            <p className="mt-3 text-brand-muted text-sm">
              BulkUsedTires.com has sent you this Non-Circumvention Agreement. Download and review the full document
              below, then type your name and today's date to sign electronically.
            </p>
          )}

          <a
            href={`${API}/agreements/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sign-agreement-pdf-link"
            className="mt-5 inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5 hover:border-brand-amber transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            {isSigned ? 'Download Your Signed Copy' : 'Download & Review Full Agreement'}
          </a>

          {isSigned ? (
            <div data-testid="sign-agreement-success" className="mt-8 flex items-start gap-3 rounded-xl border border-brand-border bg-brand-surface2 p-5">
              <CheckCircle2 className="w-6 h-6 text-brand-green mt-0.5 shrink-0" />
              <div>
                <p className="text-brand-text text-sm">
                  Signed by <strong>{agreement.signature_name}</strong> on {agreement.signature_date}.
                </p>
                <p className="text-brand-muted text-xs mt-1">A copy has been recorded by BulkUsedTires.com. Thank you.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSign} className="mt-8 space-y-4">
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Type Your Full Legal Name to Sign *</Label>
                <Input
                  data-testid="sign-agreement-name-input"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="e.g. John A. Doe"
                  className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text"
                />
              </div>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Date *</Label>
                <Input
                  type="date"
                  data-testid="sign-agreement-date-input"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text"
                />
              </div>
              <label className="flex items-start gap-2.5 text-sm text-brand-muted cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="sign-agreement-consent-checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-brand-orange"
                />
                I have read and agree to the terms of the agreement above, and I understand that typing my name
                constitutes a legally binding electronic signature.
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="sign-agreement-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors disabled:opacity-60"
              >
                <FileSignature className="w-4 h-4" />
                {submitting ? 'Signing…' : 'Sign Agreement'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
